/**
 * 배포 화면 "자정 자동 배포" 카드의 계산부 — 그리는 쪽(AutoDeployCard)과 분리해 테스트한다.
 */
import {
  SITE_BATCH_SERVICE_CATALOG,
  type AutoDeployOutcome,
  type AutoDeploySetting,
  type SaveAutoDeployRequest,
} from '../../../models/SiteBatch';

export interface AutoDeployRow {
  appname: string;
  label: string;
  checked: boolean;
  /** 고른 사내 버전. 체크했는데 비어 있으면 저장할 수 없다. */
  version: string;
}

export interface AutoDeployDraft {
  enabled: boolean;
  targetDeviceId: number | null;
  /** 배포 순서 8개 서비스 전부 — 저장된 것만 체크돼 있다. */
  rows: AutoDeployRow[];
}

/**
 * 저장된 설정 → 편집 상태. 통합서버를 아직 안 골랐으면 defaultDeviceId(통합서버가 하나뿐인
 * 현장의 그 서버)를 채운다 — 현장 배포 다이얼로그와 같다.
 */
export function draftFromSetting(s: AutoDeploySetting, defaultDeviceId: number | null): AutoDeployDraft {
  const saved = new Map(s.services.map((x) => [x.appname, x.target_version]));
  return {
    enabled: s.enabled,
    targetDeviceId: s.target_device_id || defaultDeviceId,
    rows: SITE_BATCH_SERVICE_CATALOG.map(({ appname, label }) => ({
      appname,
      label,
      checked: saved.has(appname),
      version: saved.get(appname) ?? '',
    })),
  };
}

/** PUT 본문 — 체크한 서비스만, 배포 순서대로. */
export function draftPayload(d: AutoDeployDraft): SaveAutoDeployRequest {
  return {
    enabled: d.enabled,
    target_device_id: d.targetDeviceId ?? 0,
    services: d.rows.filter((r) => r.checked).map((r) => ({ appname: r.appname, target_version: r.version })),
  };
}

/** 저장할 수 없는 이유(없으면 null) — 서버가 거절할 것을 먼저 알려 준다. */
export function draftProblem(d: AutoDeployDraft): string | null {
  const noVersion = d.rows.filter((r) => r.checked && !r.version);
  if (noVersion.length > 0) return `버전을 고르세요: ${noVersion.map((r) => r.label).join(', ')}`;
  if (d.enabled && !d.rows.some((r) => r.checked)) return '자동 배포할 서비스를 하나 이상 고르세요';
  if (d.enabled && d.targetDeviceId === null) return '배포할 통합서버를 고르세요';
  return null;
}

export interface VersionOption {
  value: string;
  /** 고른 버전이 사내 서버에서 지워짐(remote-prune) — 그대로 두면 자정 배포가 실패한다. */
  missing: boolean;
}

/** 버전 선택지 — 사내 목록(최신 먼저) + 목록에서 빠진 지금 값. 목록을 아직 못 받았으면 지금 값만. */
export function versionOptions(listed: string[] | undefined, current: string): VersionOption[] {
  const opts = (listed ?? []).map((value) => ({ value, missing: false }));
  if (current && !opts.some((o) => o.value === current)) {
    opts.unshift({ value: current, missing: listed !== undefined });
  }
  return opts;
}

export type OutcomeTone = 'success' | 'danger' | 'warn' | 'accent' | 'muted';

export const OUTCOME_META: Record<AutoDeployOutcome, { label: string; tone: OutcomeTone }> = {
  same: { label: '이미 같음', tone: 'muted' },
  started: { label: '배포 중', tone: 'accent' },
  deployed: { label: '배포 완료', tone: 'success' },
  failed: { label: '실패', tone: 'danger' },
  rolled_back: { label: '되돌림', tone: 'danger' },
  skipped: { label: '건너뜀', tone: 'warn' },
};
